import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigationType } from 'react-router';

import { DangerAlert, InfoAlert, WaitingAlert, SuccessAlert, CtaBtn, Table } from '@tmapy/style-guide';
import { useSelector, useDispatch } from '@tmapy/redux';

import { usePublishsetTagsListResource } from '../../openapi/operations/publishsetTagsList';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { useHistoryAction } from '../../hooks';
import {
  changeSubmitSuccessAlertVisibility,
  changeDeleteSuccessAlertVisibility,
} from '../../app/actions';
import { QUERY_STATE } from '../../constants';

import { tagListMessages } from './tagMessages';
import { TagListItem } from './TagListItem';

export const TagList: React.FC = () => {
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const showTagCreatedAlert = useSelector((state) => state.app.submitSuccessAlertVisible);
  const showTagDeletedAlert = useSelector((state) => state.app.deleteSuccessAlertVisible);
  const handleNewTagBtnClick = useHistoryAction('vydejNewTag');

  const { fetchMore, queryState, data } = usePublishsetTagsListResource(
    {},
    navigationType !== 'PUSH',
  );

  const tagList = data?.results ?? [];
  const tagCount = data?.count;

  const handleClearStates = useCallback(() => {
    dispatch(changeSubmitSuccessAlertVisibility(false));
    dispatch(changeDeleteSuccessAlertVisibility(false));
  }, [dispatch]);

  const handleNextTagsBtnClick = useCallback(() => {
    handleClearStates();
    if (data?.next) {
      fetchMore(data.next);
    }
  }, [handleClearStates, fetchMore, data]);

  // component unmount
  useEffect(
    () => () => {
      handleClearStates();
    },
    [handleClearStates],
  );

  return (
    <MainContent width='fit' scrollRestorationPrefix='taglist'>
      <article className='sg-u-vs-2'>
        <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
          <PageTitle>
            <FormattedMessage {...tagListMessages.pageTitle} />
          </PageTitle>
          <CtaBtn onClick={handleNewTagBtnClick} size="sml">
            <FormattedMessage {...tagListMessages.newTag} />
          </CtaBtn>
        </header>
        {showTagCreatedAlert && (
          <SuccessAlert>
            <FormattedMessage {...tagListMessages.tagCreated} />
          </SuccessAlert>
        )}
        {showTagDeletedAlert && (
          <SuccessAlert>
            <FormattedMessage {...tagListMessages.tagDeleted} />
          </SuccessAlert>
        )}
        {queryState === QUERY_STATE.WAITING && tagList.length < 1 && (
          <WaitingAlert>
            <FormattedMessage {...tagListMessages.queryStateWaiting} />
          </WaitingAlert>
        )}
        {queryState === QUERY_STATE.FAIL && tagList.length < 1 && (
          <DangerAlert>
            <FormattedMessage {...tagListMessages.queryStateFail} />
          </DangerAlert>
        )}
        {queryState === QUERY_STATE.SUCCESS && tagList.length < 1 && (
          <InfoAlert>
            <FormattedMessage {...tagListMessages.queryStateEmpty} />
          </InfoAlert>
        )}
        {tagList.length > 0 && (
          <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
            <div className='sg-a-mx--2'>
              <Table isClickable>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage {...tagListMessages.name} />
                    </th>
                    <th className='sg-a-ta-c'>
                      <FormattedMessage {...tagListMessages.isDefault} />
                    </th>
                    <th className='sg-a-ta-r'>
                      <FormattedMessage {...tagListMessages.created} />
                    </th>
                    <th className='sg-a-ta-r'>
                      <FormattedMessage {...tagListMessages.edited} />
                    </th>
                    <th className='sg-a-ta-r'>
                      <FormattedMessage {...tagListMessages.publishsetsCount} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tagList.map((tagItem) => (
                    <TagListItem key={tagItem.id} {...tagItem} />
                  ))}
                </tbody>
              </Table>
            </div>
            <LoadNextItemsBar
              count={tagList.length}
              total={tagCount!}
              queryState={queryState}
              onNextBtnClick={handleNextTagsBtnClick}
            />
          </div>
        )}
      </article>
    </MainContent>
  );
};
